import { useReadOnly } from 'slate-react';
import type { PlateElementProps } from 'platejs/react';
import { useTranslation } from 'react-i18next';
import { mdiDownload, mdiChevronRight } from '@mdi/js';
import Box from '@mui/material/Box';

import { List } from 'common/components/List';
import {
    ListItem,
    ListItemActionText,
    ListItemMedia,
    ListItemSecondaryAction,
    ListItemText,
} from 'common/components/ListItem';
import { Icon } from 'common/components/Icon';
import {
    getSafeContentUrl,
    resolveContentUrl,
} from 'common/components/ContentEditor/utils/editorHelpers';

export const EditorFile = ({
    element,
    children,
    attributes,
    className,
}: PlateElementProps) => {
    const [translate] = useTranslation();
    const readOnly = useReadOnly();

    const { url } = element as { url?: string };

    // Defense-in-depth: url can reach this render path without going
    // through upload validation (pasted HTML, legacy article content), so
    // a javascript:/data: href must be stripped here too. Content holds the
    // path, so the href is resolved against the host that serves files, the
    // same way images are.
    const safeUrl = getSafeContentUrl(url);
    const href = safeUrl ? resolveContentUrl(safeUrl) : undefined;

    // Without a usable href the item must not look clickable — otherwise
    // clicking it silently does nothing.
    const isDownloadable = readOnly && !!href;

    const itemProps = isDownloadable
        ? {
              button: true,
              component: 'a' as React.ElementType,
              href,
              target: '_blank',
          }
        : {
              component: 'div' as React.ElementType,
              disabled: readOnly,
          };

    return (
        <div className={className} {...attributes} data-slate-url={url}>
            <Box sx={{ mb: 2 }}>
                <List>
                    <ListItem {...itemProps}>
                        <ListItemMedia color="primary" contentEditable={false}>
                            <Icon path={mdiDownload} />
                        </ListItemMedia>
                        <ListItemText>{children}</ListItemText>
                        {readOnly && (
                            <ListItemActionText>
                                {translate(
                                    isDownloadable ? 'download' : 'notAvailable'
                                )}
                            </ListItemActionText>
                        )}
                        <ListItemSecondaryAction contentEditable={false}>
                            <Icon path={mdiChevronRight} size="4rem" />
                        </ListItemSecondaryAction>
                    </ListItem>
                </List>
            </Box>
        </div>
    );
};
